import React, { useEffect, useMemo, useRef, useState } from "react";

import { faArrowSquareDown, faArrowSquareUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fetchQuery, graphql } from "relay-runtime";

import { KeyboardNavigation } from "~/common";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isNotNull } from "~/lib/utils";
import { environment } from "~/models";
import { checkSentenceIsQuestion } from "~/nav/panels/AskFellow/AskFellowHooks";
import { JumpToContext } from "~/search/JumpTo/JumpToContext";
import JumpToResult, { type JumpToQueryResult } from "~/search/JumpTo/JumpToResult";
import RecentSearches from "~/search/JumpTo/RecentSearches";

import JumpToAskFellow from "./JumpToResult/JumpToAskFellow";
import JumpToResultWrapper from "./JumpToResult/JumpToResultWrapper";

import type { JumpToQuery } from "~/__generated__/JumpToQuery.graphql";

import styles from "./JumpTo.module.css";

function JumpToHandler({
	sanitizedQuery,
	isEmpty,
	onClose,
}: {
	sanitizedQuery: string;
	isEmpty: boolean;
	onClose: () => void;
}) {
	const JumpToResultComponent = () => {
		return !isEmpty ? (
			<JumpToResultWrapper
				to={`/search/?q=${encodeURIComponent(sanitizedQuery)}`}
				icon={<FontAwesomeIcon icon={faSearch} />}
				title={
					<span>
						Search <strong>&quot;{sanitizedQuery}&quot;</strong>
					</span>
				}
				index={0}
			/>
		) : null;
	};

	const isQuestion = checkSentenceIsQuestion(sanitizedQuery);

	// If the query is a question, we show ask fellow first (press enter to select it)
	// otherwise we show the search result first (press enter to select it)
	return (
		<>
			{isQuestion ? (
				<>
					<JumpToAskFellow query={sanitizedQuery} onSelect={onClose} />
					<JumpToResultComponent />
				</>
			) : (
				<>
					<JumpToResultComponent />
					<JumpToAskFellow query={sanitizedQuery} onSelect={onClose} />
				</>
			)}
		</>
	);
}

interface JumpToProps {
	query: string;
	onClose(): void;
}

const jumpToQuery = graphql`
	query JumpToQuery($query: String!) {
		jumpToSearch(query: $query) {
			edges {
				node {
					...JumpToResultSearch_result
				}
			}
		}
	}
`;

export default function JumpTo({ query, onClose }: JumpToProps) {
	const [results, setResults] = useState<Array<JumpToQueryResult>>([]);
	const [isEmpty, setIsEmpty] = useState<boolean>(false);
	const [_loading, setLoading] = useState<boolean>(false);
	const keyboardNavigationRef = useRef<KeyboardNavigation>(null);

	useEffect(() => {
		const fetchResults = async (query: string) => {
			setLoading(true);
			const data = await fetchQuery<JumpToQuery>(environment, jumpToQuery, { query }).toPromise();
			const results = data?.jumpToSearch?.edges.map(edge => edge?.node).filter(isNotNull) || [];
			setResults(results);
			setLoading(false);
			setIsEmpty(false);
			dispatchAnalyticsTrack("Jump to query", { feature: "search", query });
			keyboardNavigationRef.current?.selectElement(0);
		};

		if (!query) {
			setResults([]);
			setIsEmpty(true);
			return;
		}

		fetchResults(query);
	}, [query]);

	const contextValues = useMemo(() => {
		return {
			onClose,
		};
	}, [onClose]);

	const sanitizedQuery = useMemo(() => {
		let q = query;
		if (q) {
			return q.replace("\\#", "#");
		}
		return q;
	}, [query]);

	return (
		<>
			<div className={styles.wrapper}>
				<JumpToContext.Provider value={contextValues}>
					<KeyboardNavigation ref={keyboardNavigationRef}>
						{isEmpty ? (
							<>
								<JumpToHandler sanitizedQuery={sanitizedQuery} isEmpty={isEmpty} onClose={onClose} />
								<RecentSearches />
							</>
						) : query && !results.length && !isEmpty ? (
							<JumpToHandler sanitizedQuery={sanitizedQuery} isEmpty={isEmpty} onClose={onClose} />
						) : results.length ? (
							<>
								<JumpToHandler sanitizedQuery={sanitizedQuery} isEmpty={isEmpty} onClose={onClose} />
								<div className={styles.section}>
									<span>Top results</span>
									{results.map((result, index) => (
										<JumpToResult
											key={(result as unknown as { __id: string })["__id"]}
											result={result}
											// the first item is the search option so we add +1 to the index
											index={index + 1}
										/>
									))}
								</div>
							</>
						) : null}
					</KeyboardNavigation>
				</JumpToContext.Provider>
			</div>
			<div className={styles.footer}>
				<FontAwesomeIcon icon={faArrowSquareDown} />
				&nbsp;&nbsp;
				<FontAwesomeIcon icon={faArrowSquareUp} />
				&nbsp;&nbsp;to navigate, [return] or [enter] to select
			</div>
		</>
	);
}
